<header class="app-header">

  <!-- Left: Hamburger -->
  <button
    class="menu-btn"
    (click)="menuToggle.emit()"
    aria-label="Toggle navigation menu"
    pTooltip="Toggle menu"
    tooltipPosition="bottom"
    [showDelay]="150"
  >
    <i class="pi pi-bars"></i>
  </button>

  <!-- Company context selector — sits directly after the toggle menu icon.
       Shown only for multi-company users. Displays "code - companyName" in the
       trigger and the dropdown list, supports type-ahead search, and marks the
       active company with a green dot.
       On change: saves the full CompanyInfo and navigates to /dashboard.  -->
  <div class="company-selector" *ngIf="isMultiCompany"
       pTooltip="Active company — click to switch" tooltipPosition="bottom" [showDelay]="150">
    <i class="pi pi-building company-icon"></i>
    <p-select
      #companySelect
      class="company-select"
      [options]="allCompanies"
      [(ngModel)]="selectModel"
      (ngModelChange)="onCompanySelect($event, companySelect)"
      optionValue="compCode"
      optionLabel="companyName"
      [filter]="showCompanyFilter"
      filterBy="compCode,companyName"
      filterPlaceholder="Search company..."
      [appendTo]="'body'"
      styleClass="company-p-select"
      aria-label="Select active company"
    >
      <!-- Trigger: active-status dot + name of the active company -->
      <ng-template let-c pTemplate="selectedItem">
        <span class="company-option-label">
          <span class="active-dot is-active" aria-hidden="true"></span>
          <span class="company-name">{{ c.companyName || c.compCode }}</span>
        </span>
      </ng-template>

      <!-- List item: green dot for the active company, then name -->
      <ng-template let-c pTemplate="item">
        <span class="company-option-label">
          <span
            class="active-dot"
            [class.is-active]="c.compCode === selectedCompany?.compCode"
            aria-hidden="true"
          ></span>
          <span class="company-name">{{ c.companyName || c.compCode }}</span>
        </span>
      </ng-template>
    </p-select>
  </div>

  <!-- Single-company badge — read-only, shows company name with active-status dot -->
  <div class="company-badge" *ngIf="!isMultiCompany && selectedCompany"
       pTooltip="Active company" tooltipPosition="bottom" [showDelay]="150">
    <i class="pi pi-building"></i>
    <span class="active-dot is-active" aria-hidden="true"></span>
    <span>{{ selectedCompany!.companyName || selectedCompany!.compCode }}</span>
  </div>

  <!-- Spacer — pushes the actions to the right while the company selector stays
       next to the toggle menu icon on the left. -->
  <div class="header-spacer"></div>

  <!-- Right: Actions -->
  <div class="header-actions">

    <!-- Search — opens a popup search modal on click -->
    <button class="action-btn"
            (click)="toggleSearch()"
            aria-label="Search"
            pTooltip="Search"
            tooltipPosition="bottom"
            [showDelay]="150"
            [attr.aria-expanded]="searchOpen">
      <i class="pi pi-search search-trigger-icon"></i>
    </button>

    <!-- Notifications -->
    <div class="notification-menu">
      <button
        class="action-btn"
        (click)="toggleNotifications()"
        aria-label="Notifications"
        pTooltip="Notifications"
        tooltipPosition="bottom"
        [showDelay]="150"
        [attr.aria-expanded]="notificationsOpen"
        aria-haspopup="true"
      >
        <!-- Filled bell (PrimeIcons has no pi-bell-fill, so use an inline SVG
             that inherits the button's colour via currentColor). -->
        <svg class="bell-icon" viewBox="0 0 24 24" width="1em" height="1em"
             fill="currentColor" aria-hidden="true" focusable="false">
          <path d="M12 2a2 2 0 0 1 2 2v.29c2.97.88 5 3.64 5 6.71v3.38l1.55 2.33A1 1 0 0 1 19.72 20H4.28a1 1 0 0 1-.83-1.55L5 16.12V11c0-3.07 2.03-5.83 5-6.71V4a2 2 0 0 1 2-2z"/>
          <path d="M9.5 20.5h5a2.5 2.5 0 0 1-5 0z"/>
        </svg>
        <span class="badge" *ngIf="unreadCount > 0" aria-hidden="true">
          {{ unreadCount > 99 ? '99+' : unreadCount }}
        </span>
      </button>

      <!-- Dropdown panel -->
      <div class="notification-dropdown" *ngIf="notificationsOpen" role="menu">
        <div class="notification-header">
          <span class="notification-title"><i class="pi pi-bell"></i> Notifications</span>
          <span class="notification-count" *ngIf="unreadCount > 0">{{ unreadCount }} unread</span>
        </div>

        <div class="notification-list">
          <!-- Loading -->
          <div class="notification-loading" *ngIf="loadingNotifications">
            <i class="pi pi-spin pi-spinner"></i>
            <span>Loading...</span>
          </div>

          <!-- Empty -->
          <div class="notification-empty" *ngIf="!loadingNotifications && notifications.length === 0">
            <i class="pi pi-inbox"></i>
            <span>No notifications</span>
          </div>

          <!-- Items -->
          <button
            *ngFor="let n of notifications; trackBy: trackByNotificationId"
            class="notification-item notif--{{ notifSeverity(n) }}"
            [class.unread]="!n.isRead"
            (click)="onNotificationClick(n)"
            role="menuitem"
          >
            <!-- Leading colour-coded icon badge (type hint) -->
            <span class="notif-icon" aria-hidden="true">
              <i [class]="notifIcon(n)"></i>
            </span>

            <div class="notification-body">
              <span class="notification-item-title">
                {{ n.title }}
                <span class="unread-dot" *ngIf="!n.isRead" aria-hidden="true"></span>
              </span>
              <span class="notification-message">{{ n.message }}</span>
              <div class="notification-meta">
                <span class="notif-pill">{{ notifLabel(n) }}</span>
                <span class="notification-time"><i class="pi pi-clock"></i> {{ n.createdOn | date: 'short' }}</span>
              </div>
            </div>

            <span
              class="mark-read"
              *ngIf="!n.isRead"
              (click)="markRead(n, $event)"
              title="Mark as read"
              aria-label="Mark as read"
            >
              <i class="pi pi-check"></i>
            </span>
          </button>
        </div>
      </div>
    </div>

    <!-- Help -->
    <button class="action-btn"
            aria-label="Help"
            pTooltip="Help"
            tooltipPosition="bottom"
            [showDelay]="150">
      <i class="pi pi-question-circle help-icon"></i>
    </button>

    <!-- User profile dropdown -->
    <div class="user-menu" (click)="toggleUserMenu()" (keydown.enter)="toggleUserMenu()" tabindex="0" role="button" [attr.aria-expanded]="userMenuOpen" aria-haspopup="true">
      <div class="user-avatar">
        {{ userInitials }}
      </div>
      <div class="user-info" aria-hidden="true">
        <span class="user-name">{{ currentUser?.username || 'User' }}</span>
        <span class="user-role">{{ currentUser?.roleName || 'Administrator' }}</span>
      </div>
      <i class="pi pi-chevron-down chevron" [class.open]="userMenuOpen" aria-hidden="true"></i>

      <!-- Dropdown -->
      <div class="user-dropdown" *ngIf="userMenuOpen" role="menu">
        <div class="dropdown-header">
          <div class="dropdown-avatar">{{ userInitials }}</div>
          <div>
            <div class="dropdown-name">{{ currentUser?.username }}</div>
            <div class="dropdown-role">{{ currentUser?.roleName }}</div>
          </div>
        </div>
        <div class="dropdown-divider"></div>
        <button class="dropdown-item" role="menuitem">
          <i class="pi pi-user"></i>
          <span>My Profile</span>
        </button>
        <button class="dropdown-item" role="menuitem">
          <i class="pi pi-cog"></i>
          <span>Account Settings</span>
        </button>
        <div class="dropdown-divider"></div>
        <button class="dropdown-item logout" role="menuitem" (click)="onLogout()">
          <i class="pi pi-sign-out"></i>
          <span>Sign Out</span>
        </button>
      </div>
    </div>

  </div>
</header>

<!-- Click-outside overlay for user menu -->
<div class="menu-backdrop" *ngIf="userMenuOpen" (click)="userMenuOpen = false"></div>

<!-- Click-outside overlay for notification dropdown -->
<div class="menu-backdrop" *ngIf="notificationsOpen" (click)="notificationsOpen = false"></div>

<!-- ── Company switch confirmation dialog ──────────────────────────────────── -->
<div class="confirm-backdrop" *ngIf="pendingCompany" (click)="cancelSwitch()">
  <div class="confirm-dialog" (click)="$event.stopPropagation()">

    <div class="confirm-icon warn">
      <i class="pi pi-building"></i>
    </div>

    <h3 class="confirm-title">Do you want to switch the company?</h3>

    <p class="confirm-body">
      Switch to
      <strong>{{ pendingCompany.companyName || pendingCompany.compCode }}</strong>?
    </p>

    <div class="confirm-actions">
      <button class="btn-secondary" (click)="cancelSwitch()">
        No
      </button>
      <button class="btn-primary" (click)="confirmSwitch()">
        <i class="pi pi-check"></i>
        <span>Yes</span>
      </button>
    </div>

  </div>
</div>

<!-- ── Search popup modal ──────────────────────────────────────────────────── -->
<!-- Opened from the header search icon. Clicking the backdrop or Escape closes
     it; submitting runs onSearchSubmit(). -->
<div class="search-backdrop" *ngIf="searchOpen" (click)="closeSearch()">
  <div class="search-dialog"
       role="dialog"
       aria-modal="true"
       aria-label="Search"
       (click)="$event.stopPropagation()">

    <div class="search-dialog__header">
      <span class="search-dialog__title"><i class="pi pi-search"></i> Search</span>
      <button type="button"
              class="search-dialog__close"
              aria-label="Close search"
              (click)="closeSearch()">
        <i class="pi pi-times"></i>
      </button>
    </div>

    <form class="search-dialog__body" (ngSubmit)="onSearchSubmit()">
      <div class="search-dialog__field">
        <i class="pi pi-search search-dialog__field-icon"></i>
        <input #searchBox
               type="text"
               pInputText
               class="search-dialog__input"
               [(ngModel)]="searchTerm"
               name="headerSearch"
               placeholder="Type to search…"
               autocomplete="off"
               aria-label="Search term" />
        <button *ngIf="searchTerm"
                type="button"
                class="search-dialog__clear"
                aria-label="Clear"
                (click)="searchTerm = ''; searchBox.focus()">
          <i class="pi pi-times"></i>
        </button>
      </div>

      <div class="search-dialog__actions">
        <button type="button" class="btn-secondary" (click)="closeSearch()">Cancel</button>
        <button type="submit" class="btn-primary" [disabled]="!searchTerm.trim()">
          <i class="pi pi-search"></i>
          <span>Search</span>
        </button>
      </div>
    </form>

  </div>
</div>
